/**
 * Tiny localStorage persistence helper for Redux — replaces `redux-persist`.
 *
 * Storage format is backward-compatible with redux-persist's root persistor
 * (key `persist:<key>`, value is a JSON object whose values are themselves
 * JSON-stringified slice states, plus a `_persist` marker). This means users
 * with state stored by the previous redux-persist setup are not logged out.
 */

import type { Store, UnknownAction } from "@reduxjs/toolkit"

interface PersistConfig<S> {
	key: string
	version: number
	migrate?: (state: any, version: number) => any
	whitelist?: (keyof S)[]
}

interface PersistMeta {
	version: number
}

const META_KEY = "_persist"

function storageKey(key: string): string {
	return `persist:${key}`
}

function readStored(key: string): Record<string, string> | null {
	try {
		const raw = localStorage.getItem(storageKey(key))
		if (!raw) return null
		return JSON.parse(raw) as Record<string, string>
	} catch {
		return null
	}
}

function writeStored(key: string, value: Record<string, string>): void {
	try {
		localStorage.setItem(storageKey(key), JSON.stringify(value))
	} catch {
		// Quota exceeded or storage unavailable — silently skip.
	}
}

/**
 * Loads + migrates persisted state for use as Redux `preloadedState`.
 * Returns `undefined` when nothing is stored, letting the reducer use defaults.
 */
export function loadPersistedState<S extends Record<string, unknown>>(
	config: PersistConfig<S>,
): Partial<S> | undefined {
	const stored = readStored(config.key)
	if (!stored) return undefined

	const parsed: Record<string, unknown> = {}
	for (const [k, v] of Object.entries(stored)) {
		if (k === META_KEY) continue
		try {
			parsed[k] = JSON.parse(v)
		} catch {
			// Skip slices with corrupted serialization.
		}
	}

	let meta: PersistMeta = { version: -1 }
	if (stored[META_KEY]) {
		try {
			meta = JSON.parse(stored[META_KEY]) as PersistMeta
		} catch {
			// Treat as version -1, force full migration chain.
		}
	}

	let migrated: any = parsed
	if (config.migrate && meta.version !== config.version) {
		migrated = config.migrate(parsed, meta.version)
	}

	return migrated as Partial<S>
}

export interface Persistor {
	/** Stop persisting future state changes. */
	stop: () => void
	/** Remove the persisted state from storage. */
	purge: () => void
}

/**
 * Subscribes to a store and persists the (whitelisted) state on every change.
 */
export function persistStore<S extends Record<string, unknown>>(
	store: Store<S, UnknownAction>,
	config: PersistConfig<S>,
): Persistor {
	const persist = () => {
		const state = store.getState()
		const out: Record<string, string> = {
			[META_KEY]: JSON.stringify({ version: config.version }),
		}
		const keys = config.whitelist ?? (Object.keys(state) as (keyof S)[])
		for (const k of keys) {
			out[k as string] = JSON.stringify(state[k])
		}
		writeStored(config.key, out)
	}

	persist()
	const unsubscribe = store.subscribe(persist)

	return {
		stop: unsubscribe,
		purge: () => {
			try {
				localStorage.removeItem(storageKey(config.key))
			} catch {
				// Ignore — storage unavailable.
			}
		},
	}
}
